import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../SupabaseClient.tsx";
import "./TaxCenter.css";
import {
  PiFileCsv,
  PiFileZip,
  PiMagnifyingGlassPlus,
  PiPlus,
} from "react-icons/pi";
import ActionButton from "../components/ActionButton.tsx";
import ReadOnlyBanner from "../components/ReadOnlyBanner.tsx";
import { Lapse } from "../utils/checkout.ts";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import type { Attachment } from "../types/Order.ts";

interface Reimbursement {
  id: number;
  order_id: number;
  orders: {
    name: string;
    beneficiary: string;
  };
  attachments?: Attachment[];
  reimburse_date: string;
  reimburse_amount: number;
}

interface TaxCenterProps {
  // Set when the account is read-only because a trial or subscription has ended
  lapse?: Lapse | null;
}

const TaxCenter: React.FC<TaxCenterProps> = ({ lapse = null }) => {
  const [years, setYears] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [reimbursements, setReimbursements] = useState<Reimbursement[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchYears = async () => {
      setLoading(true);
      setError("");
      const { data, error } = await supabase
        .from("order_reimburse")
        .select("reimburse_date")
        .order("reimburse_date", { ascending: false });
      if (error) {
        setError("Failed to fetch years.");
        setLoading(false);
        return;
      }
      const yearSet = new Set<number>();
      (data || []).forEach((r: any) => {
        if (r.reimburse_date) {
          yearSet.add(new Date(r.reimburse_date).getFullYear());
        }
      });
      const yearArr = Array.from(yearSet).sort((a, b) => b - a);
      setYears(yearArr);
      setSelectedYear(yearArr.length > 0 ? yearArr[0] : null);
      setLoading(false);
    };
    fetchYears();
  }, []);

  useEffect(() => {
    if (!selectedYear) return;
    setLoading(true);
    setError("");
    const fetchReimbursements = async () => {
      const { data, error } = await supabase
        .from("order_reimburse")
        .select(
          "id, order_id, reimburse_date, reimburse_amount, orders(name, beneficiary)"
        )
        .gte("reimburse_date", `${selectedYear}-01-01`)
        .lte("reimburse_date", `${selectedYear}-12-31`)
        .order("reimburse_date", { ascending: true });
      if (error) {
        setError("Failed to fetch reimbursements.");
        setLoading(false);
        return;
      }

      // Fetch attachments for all orders
      const orderIds = (data || []).map((r: any) => r.order_id);
      const { data: attachmentsData, error: attachmentsError } = await supabase
        .from("order_attachments")
        .select(
          "id, order_id, storage_path, original_filename, mime_type, is_primary, created_at"
        )
        .in("order_id", orderIds);

      // The ZIP export uses these, so it would otherwise download without files
      if (attachmentsError) {
        console.error("Error fetching attachments:", attachmentsError);
        setError("Failed to fetch receipt files.");
        setLoading(false);
        return;
      }

      // Group attachments by order_id
      const attachmentsByOrderId: Record<string, Attachment[]> = {};
      (attachmentsData || []).forEach((attachment: Attachment) => {
        if (!attachmentsByOrderId[attachment.order_id]) {
          attachmentsByOrderId[attachment.order_id] = [];
        }
        attachmentsByOrderId[attachment.order_id].push(attachment);
      });

      // Add attachments to reimbursements
      const reimbursementsWithAttachments = (data || []).map((r: any) => ({
        ...r,
        attachments: attachmentsByOrderId[r.order_id] || [],
      }));

      setReimbursements(reimbursementsWithAttachments);
      setLoading(false);
    };
    fetchReimbursements();
  }, [selectedYear]);

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedYear(Number(e.target.value));
  };

  const handleExportZip = async () => {
    setDownloading(true);
    const zip = new JSZip();

    // Fetch each file from Supabase storage and add to zip
    for (const reimbursement of reimbursements) {
      if (reimbursement.attachments && reimbursement.attachments.length > 0) {
        // Add all attachments for this order
        for (const attachment of reimbursement.attachments) {
          const { data, error } = await supabase.storage
            .from("order_receipts")
            .download(attachment.storage_path);

          if (!error && data) {
            const fileBlob = await data.arrayBuffer();
            // Use reimbursement id + attachment filename to avoid duplicates
            const filename =
              reimbursement.attachments.length > 1
                ? `${reimbursement.order_id}_${attachment.original_filename}`
                : attachment.original_filename;
            zip.file(filename, fileBlob);
          }
        }
      }
    }

    // Generate zip and trigger download
    zip
      .generateAsync({ type: "blob" })
      .then((content) => {
        saveAs(content, "receipts_" + selectedYear + ".zip");
        setDownloading(false);
      })
      .catch(() => {
        setDownloading(false);
      });
  };

  const handleDownloadCsv = async () => {
    setDownloading(true);
    setError("");
    try {
      // No receipt_url, so just download a CSV summary
      const csvRows = ["Date Reimbursed,Description,Beneficiary,Reimbursed"];
      reimbursements.forEach((r) => {
        csvRows.push(
          `${new Date(r.reimburse_date).toLocaleDateString()},${r.orders?.name || "N/A"
          },${r.orders?.beneficiary || "N/A"},${r.reimburse_amount.toFixed(2)}`
        );
      });
      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reimbursements_${selectedYear}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError("Failed to download receipts. Please try again.");
    }
    setDownloading(false);
  };

  return (
    <div className="global-page-screen">
      <div className="page">
        <div className="page-header">
          <div className="page-header-title">
            <h1>Tax Center</h1>
            <p>
              Review all of your HSA reimbursements for a given year. Download a
              zipped bundle of all reimbursed receipts and supporting documents
              for your records or tax filing.
            </p>
          </div>
        </div>
        {lapse && <ReadOnlyBanner lapse={lapse} />}
        {!loading && (
          <>
            <div className="taxcenter-container">
              {years.length !== 0 ? (
                <>
                  <div className="taxcenter-summary">
                    <div className="taxcenter-total">
                      <div className="total-label">Total Reimbursed ({selectedYear})</div>
                      <div className="total-amount">
                        $
                        {reimbursements
                          .reduce((sum, r) => sum + r.reimburse_amount, 0)
                          .toFixed(2)}
                      </div>
                      <div className="tax-form-note">
                        This amount should be reported in Box 1 of Form 1099-SA
                      </div>
                    </div>
                  </div>
                  <div className="taxcenter-controls">
                    <div className="taxcenter-conrols-actions">
                      <label htmlFor="year-select">Year:</label>
                      <select
                        id="year-select"
                        value={selectedYear || ""}
                        onChange={handleYearChange}
                        disabled={loading || years.length === 0}
                      >
                        {years.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="taxcenter-conrols-buttons">
                      <ActionButton
                        icon={<PiFileZip />}
                        text={
                          downloading ? "Downloading..." : "Download Receipts"
                        }
                        onClick={handleExportZip}
                        disabled={downloading || reimbursements.length === 0}
                        responsive={true}
                        secondary={true}
                      />
                      <ActionButton
                        icon={<PiFileCsv />}
                        text={
                          downloading ? "Downloading..." : "Download Summary"
                        }
                        onClick={handleDownloadCsv}
                        disabled={downloading || reimbursements.length === 0}
                        responsive={true}
                      />
                    </div>
                  </div>
                  {error && <div className="error-message">{error}</div>}
                </>
              ) : (
                <div className="container-empty">
                  <p>
                    You haven't reimbursed any eligible expenses yet. Tax
                    information will be available once you start logging
                    expenses.
                  </p>
                  {!lapse && (
                    <ActionButton
                      onClick={() => navigate("/reimburse")}
                      text="Reimburse Now"
                      icon={<PiPlus />}
                    />
                  )}
                </div>
              )}
            </div>
            {reimbursements.length > 0 && (
              <div className="receipt-table">
                <table style={{ tableLayout: "auto" }}>
                  <thead>
                    <tr>
                      <th>Date Reimbursed</th>
                      <th>Description</th>
                      <th>Beneficiary</th>
                      <th>Reimbursed</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reimbursements.map((r) => (
                      <tr key={r.id}>
                        <td>
                          {new Date(r.reimburse_date).toLocaleDateString()}
                        </td>
                        <td>{r.orders ? r.orders.name : "N/A"}</td>
                        <td>{r.orders ? r.orders.beneficiary : "N/A"}</td>
                        <td>${r.reimburse_amount.toFixed(2)}</td>
                        <td>
                          <ActionButton
                            text="View"
                            onClick={() =>
                              navigate(`/receipt-library/${r.order_id}`)
                            }
                            secondary={true}
                            responsive={true}
                            icon={<PiMagnifyingGlassPlus />}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TaxCenter;
